/**
 * Tickets Page
 * ============
 * صفحة البلاغات والتذاكر وإدارتها - from database
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card } from '../../../lib/ui/Card';
import { Table } from '../../../lib/ui/Table';
import { Button } from '../../../lib/ui/Button';
import { SearchBox } from '../../../lib/ui/SearchBox';
import { Modal } from '../../../lib/ui/Modal';
import { Dropdown } from '../../../lib/ui/Dropdown';
import { Input } from '../../../lib/ui/Input';
import { ticketsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useDialog } from '../../../context/DialogContext';
import './TicketsPage.css';
import { validateFiles, scanMessageText } from '../../../lib/security/fileSecurity';

const renderText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color:'#0ABAB5', textDecoration:'underline', wordBreak:'break-all' }}>{part}</a>
      : part
  );
};

const Icons = {
  ticket: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
};

const TicketsPage = () => {
  const toast = useToast();
  const { confirm } = useDialog();

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });
  const [newMessage,     setNewMessage]     = useState('');
  const [adminFiles,     setAdminFiles]     = useState([]);
  const [processing,     setProcessing]     = useState(false);
  const msgBottomRef       = useRef(null);
  const msgsContainerRef    = useRef(null);
  const [newBadges, setNewBadges] = useState({}); // ticketId → true
  const prevBadgeKeysRef = useRef(''); // serialized keys to detect change

  const getReadCount = (id) => parseInt(localStorage.getItem(`wt_read_${id}`) || '0', 10);
  const setReadCount = (id, n) => localStorage.setItem(`wt_read_${id}`, String(n));

  // Web Audio beep — no external file needed
  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine'; osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
    } catch {}
  };

  // Smart list polling — runs only when tab is visible
  useEffect(() => {
    const poll = async () => {
      if (document.hidden) return;
      try {
        const data = await ticketsService.getAll().catch(() => null);
        if (!data) return;
        const incoming = data.items || [];
        const badges = {};
        let hasNew = false;
        incoming.forEach(t => {
          const cur  = t.message_count ?? 0;
          const seen = getReadCount(t.id);
          if (cur > seen) {
            badges[t.id] = true;
            hasNew = true;
          }
        });
        const newKeys = Object.keys(badges).sort().join(',');
        if (hasNew && newKeys !== prevBadgeKeysRef.current) {
          playBeep();
          prevBadgeKeysRef.current = newKeys;
        }
        setNewBadges(hasNew ? badges : {});
        incoming.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        setTickets(incoming);
      } catch {}
    };
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showDetailModal) {
      setTimeout(() => msgBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 120);
    }
  }, [showDetailModal, selectedTicket?.messages?.length]);

  useEffect(() => {
    if (!showDetailModal || !selectedTicket?.id) return;
    const interval = setInterval(async () => {
      try {
        const updated = await ticketsService.getById(selectedTicket.id);
        setSelectedTicket(prev => prev?.id === updated.id ? updated : prev);
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [showDetailModal, selectedTicket?.id]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ticketsData, statsData] = await Promise.all([
        ticketsService.getAll().catch(() => ({ items: [] })),
        ticketsService.getStats().catch(() => ({})),
      ]);
      const items = ticketsData.items || [];
      // On first load: set read baseline for tickets never seen before (no alert)
      items.forEach(t => {
        const cur = t.message_count ?? 0;
        if (localStorage.getItem(`wt_read_${t.id}`) === null) {
          setReadCount(t.id, cur);
        }
      });
      items.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setTickets(items);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject) {
      toast.error('الموضوع مطلوب');
      return;
    }
    setProcessing(true);
    try {
      await ticketsService.create(newTicket);
      toast.success('تم إنشاء التذكرة بنجاح');
      setShowCreateModal(false);
      setNewTicket({ subject: '', description: '', category: 'general', priority: 'medium' });
      loadData();
    } catch (error) {
      toast.error('فشل في إنشاء التذكرة');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateStatus = async (ticket, newStatus) => {
    try {
      await ticketsService.updateStatus(ticket.id, newStatus);
      toast.success('تم تحديث حالة التذكرة');
      loadData();
      if (selectedTicket?.id === ticket.id) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      toast.error('فشل في تحديث حالة التذكرة');
    }
  };

  const handleAddMessage = async () => {
    if (!newMessage.trim() && adminFiles.length === 0) return;
    if (!selectedTicket) return;
    const textCheck = scanMessageText(newMessage);
    if (!textCheck.safe) { toast.error(textCheck.reason); return; }
    if (textCheck.warning) toast.error(`تحذير: ${textCheck.warning}`);
    if (adminFiles.length > 0) {
      const fileCheck = await validateFiles(adminFiles);
      if (!fileCheck.safe) { toast.error(fileCheck.reason); return; }
    }
    setProcessing(true);
    try {
      if (adminFiles.length > 0) {
        const API = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';
        const token = localStorage.getItem('watrly_admin_token') || localStorage.getItem('token');
        const fd = new FormData();
        fd.append('message', newMessage.trim() || '...');
        fd.append('sender_type', 'admin');
        adminFiles.forEach(f => fd.append('files', f));
        const res = await fetch(`${API}/tickets/${selectedTicket.id}/messages/upload`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.detail || 'خطأ'); }
      } else {
        await ticketsService.addMessage(selectedTicket.id, { message: newMessage, sender_type: 'admin' });
      }
      toast.success('تم إرسال الرد');
      setNewMessage('');
      setAdminFiles([]);
      const updated = await ticketsService.getById(selectedTicket.id);
      setSelectedTicket(updated);
    } catch (error) {
      toast.error('فشل في إرسال الرد: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (ticket) => {
    if (!await confirm({ title: 'حذف التذكرة', message: 'هل أنت متأكد من حذف هذه التذكرة؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    try {
      await ticketsService.delete(ticket.id);
      toast.success('تم حذف التذكرة');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error('فشل في حذف التذكرة');
    }
  };

  const openTicketDetail = async (ticket) => {
    // mark as read in localStorage
    const cur = ticket.message_count ?? 0;
    setReadCount(ticket.id, cur);
    setNewBadges(prev => { const n = { ...prev }; delete n[ticket.id]; return n; });
    try {
      const detail = await ticketsService.getById(ticket.id);
      setSelectedTicket(detail);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('فشل في تحميل تفاصيل التذكرة');
    }
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      general: 'استفسار عام', order: 'مشكلة في طلب',
      payment: 'مشكلة في الدفع', account: 'مشكلة في الحساب', other: 'أخرى',
      technical: 'تقني', billing: 'مالي', complaint: 'شكوى', suggestion: 'اقتراح',
    };
    return labels[cat] || cat || 'عام';
  };

  const getStatusLabel = (status) => {
    const labels = { open: 'مفتوح', in_progress: 'قيد المعالجة', resolved: 'تم الحل', closed: 'مغلق' };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = { low: 'منخفض', medium: 'متوسط', high: 'عالي', urgent: 'عاجل' };
    return labels[priority] || priority;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      const matchesSearch = t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || t.priority === priorityFilter;
      return matchesSearch && matchesCategory && matchesStatus && matchesPriority;
    });
  }, [tickets, searchQuery, categoryFilter, statusFilter, priorityFilter]);

  const categoryOptions = [
    { value: 'all',      label: 'جميع الفئات' },
    { value: 'general',  label: 'استفسار عام' },
    { value: 'order',    label: 'مشكلة في طلب' },
    { value: 'payment',  label: 'مشكلة في الدفع' },
    { value: 'account',  label: 'مشكلة في الحساب' },
    { value: 'other',    label: 'أخرى' },
  ];

  const statusOptions = [
    { value: 'all', label: 'جميع الحالات' },
    { value: 'open', label: 'مفتوح' },
    { value: 'in_progress', label: 'قيد المعالجة' },
    { value: 'resolved', label: 'تم الحل' },
    { value: 'closed', label: 'مغلق' },
  ];

  const priorityOptions = [
    { value: 'all', label: 'جميع الأولويات' },
    { value: 'low', label: 'منخفض' },
    { value: 'medium', label: 'متوسط' },
    { value: 'high', label: 'عالي' },
    { value: 'urgent', label: 'عاجل' },
  ];

  const columns = [
    { key: 'ticket_number', title: 'رقم التذكرة', render: (val) => <span className="watrly-tickets__number">{val}</span> },
    { key: 'subject', title: 'الموضوع', render: (val, row) => (
      <span className="watrly-tickets__subject" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
        {newBadges[row.id] && (
          <span style={{ width:8, height:8, borderRadius:'50%', background:'#ef4444', flexShrink:0, boxShadow:'0 0 6px #ef4444', display:'inline-block', animation:'pulse-red 1.2s ease-in-out infinite' }}/>
        )}
        {val}
        {row.created_by_type === 'customer' && <small style={{ marginRight: '0.4rem', color: '#0ABAB5' }}>• عميل</small>}
      </span>
    )},
    { key: 'customer_name', title: 'العميل', render: (val, row) => val ? (
      <div>
        <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1E3A3A' }}>{val}</div>
        {row.customer_phone && <div style={{ fontSize: '0.75rem', color: '#737373', direction: 'ltr' }}>{row.customer_phone}</div>}
      </div>
    ) : <span style={{ color: '#A3A3A3', fontSize: '0.8rem' }}>—</span> },
    { key: 'category', title: 'الفئة', render: (val) => <span className={`watrly-tickets__category watrly-tickets__category--${val}`}>{getCategoryLabel(val)}</span> },
    { key: 'priority', title: 'الأولوية', render: (val) => <span className={`watrly-tickets__priority watrly-tickets__priority--${val}`}>{getPriorityLabel(val)}</span> },
    { key: 'status', title: 'الحالة', render: (val) => <span className={`watrly-tickets__status watrly-tickets__status--${val}`}>{getStatusLabel(val)}</span> },
    { key: 'created_at', title: 'التاريخ', render: (val) => formatDate(val) },
    { 
      key: 'actions', 
      title: '', 
      sortable: false, 
      render: (_, row) => (
        <div className="watrly-tickets__actions">
          <button className="watrly-tickets__action-btn" onClick={() => openTicketDetail(row)} title="عرض">{Icons.eye}</button>
          {row.status === 'open' && (
            <button className="watrly-tickets__action-btn watrly-tickets__action-btn--progress" onClick={() => handleUpdateStatus(row, 'in_progress')} title="بدء المعالجة">{Icons.clock}</button>
          )}
          {(row.status === 'open' || row.status === 'in_progress') && (
            <button className="watrly-tickets__action-btn watrly-tickets__action-btn--resolve" onClick={() => handleUpdateStatus(row, 'resolved')} title="تم الحل">{Icons.check}</button>
          )}
          {row.status !== 'closed' && (
            <button className="watrly-tickets__action-btn watrly-tickets__action-btn--delete" onClick={() => handleUpdateStatus(row, 'closed')} title="إغلاق التذكرة" style={{ color:'#94a3b8' }}>{Icons.x}</button>
          )}
          {row.status === 'closed' && (
            <button className="watrly-tickets__action-btn watrly-tickets__action-btn--progress" onClick={() => handleUpdateStatus(row, 'open')} title="إعادة فتح" style={{ color:'#f59e0b' }}>{Icons.alert}</button>
          )}
          <button className="watrly-tickets__action-btn watrly-tickets__action-btn--delete" onClick={() => handleDelete(row)} title="حذف">{Icons.trash}</button>
        </div>
      )
    },
  ];

  return (
    <div className="watrly-tickets">
      <div className="watrly-tickets__header">
        <div>
          <h1 className="watrly-tickets__title">التذاكر والبلاغات</h1>
          <p className="watrly-tickets__subtitle">إدارة تذاكر الدعم الفني والبلاغات • البيانات من قاعدة البيانات</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>+ تذكرة جديدة</Button>
      </div>

      <div className="watrly-tickets__stats">
        <div className="watrly-tickets__stat-card watrly-tickets__stat-card--open">
          <div className="watrly-tickets__stat-icon">{Icons.ticket}</div>
          <div className="watrly-tickets__stat-content">
            <span className="watrly-tickets__stat-value">{stats.open || 0}</span>
            <span className="watrly-tickets__stat-label">مفتوح</span>
          </div>
        </div>
        <div className="watrly-tickets__stat-card watrly-tickets__stat-card--progress">
          <div className="watrly-tickets__stat-icon">{Icons.clock}</div>
          <div className="watrly-tickets__stat-content">
            <span className="watrly-tickets__stat-value">{stats.in_progress || 0}</span>
            <span className="watrly-tickets__stat-label">قيد المعالجة</span>
          </div>
        </div>
        <div className="watrly-tickets__stat-card watrly-tickets__stat-card--resolved">
          <div className="watrly-tickets__stat-icon">{Icons.check}</div>
          <div className="watrly-tickets__stat-content">
            <span className="watrly-tickets__stat-value">{stats.resolved || 0}</span>
            <span className="watrly-tickets__stat-label">تم الحل</span>
          </div>
        </div>
        <div className="watrly-tickets__stat-card watrly-tickets__stat-card--high">
          <div className="watrly-tickets__stat-icon">{Icons.alert}</div>
          <div className="watrly-tickets__stat-content">
            <span className="watrly-tickets__stat-value">{stats.high_priority || 0}</span>
            <span className="watrly-tickets__stat-label">عالي الأولوية</span>
          </div>
        </div>
      </div>

      <Card padding="md" className="watrly-tickets__filters-card">
        <div className="watrly-tickets__filters">
          <SearchBox value={searchQuery} onChange={setSearchQuery} placeholder="بحث..." className="watrly-tickets__search" />
          <div className="watrly-tickets__filter-group">
            <Dropdown options={categoryOptions} value={categoryFilter} onChange={setCategoryFilter} />
            <Dropdown options={statusOptions} value={statusFilter} onChange={setStatusFilter} />
            <Dropdown options={priorityOptions} value={priorityFilter} onChange={setPriorityFilter} />
          </div>
        </div>
      </Card>

      <div className="watrly-tickets__results-info">عرض {filteredTickets.length} من {tickets.length} تذكرة</div>

      <Card padding="none" className="watrly-tickets__table-card">
        <Table columns={columns} data={filteredTickets} loading={loading} emptyMessage="لا يوجد تذاكر" hoverable />
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} title={`تذكرة ${selectedTicket?.ticket_number || ''}`} size="lg">
        {selectedTicket && (
          <div className="watrly-tickets__detail">
            <div className="watrly-tickets__detail-header">
              <h3>{selectedTicket.subject}</h3>
              <div className="watrly-tickets__detail-meta">
                <span className={`watrly-tickets__status watrly-tickets__status--${selectedTicket.status}`}>{getStatusLabel(selectedTicket.status)}</span>
                <span className={`watrly-tickets__priority watrly-tickets__priority--${selectedTicket.priority}`}>{getPriorityLabel(selectedTicket.priority)}</span>
              </div>
            </div>

            {/* Status actions */}
            <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
              {selectedTicket.status === 'closed' ? (
                <button onClick={() => handleUpdateStatus(selectedTicket, 'open')} style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.4rem 0.85rem', borderRadius:'0.5rem', border:'1px solid rgba(245,158,11,0.35)', background:'rgba(245,158,11,0.08)', color:'#f59e0b', fontSize:'0.75rem', fontWeight:700, cursor:'pointer', fontFamily:"'Cairo',sans-serif" }}>
                  {Icons.alert} إعادة فتح التذكرة
                </button>
              ) : (
                <>
                  {selectedTicket.status === 'open' && (
                    <button onClick={() => handleUpdateStatus(selectedTicket, 'in_progress')} style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.4rem 0.85rem', borderRadius:'0.5rem', border:'1px solid rgba(56,189,248,0.35)', background:'rgba(56,189,248,0.08)', color:'#38bdf8', fontSize:'0.75rem', fontWeight:700, cursor:'pointer', fontFamily:"'Cairo',sans-serif" }}>
                      {Icons.clock} بدء المعالجة
                    </button>
                  )}
                  {(selectedTicket.status === 'open' || selectedTicket.status === 'in_progress') && (
                    <button onClick={() => handleUpdateStatus(selectedTicket, 'resolved')} style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.4rem 0.85rem', borderRadius:'0.5rem', border:'1px solid rgba(34,197,94,0.35)', background:'rgba(34,197,94,0.08)', color:'#22c55e', fontSize:'0.75rem', fontWeight:700, cursor:'pointer', fontFamily:"'Cairo',sans-serif" }}>
                      {Icons.check} تم الحل
                    </button>
                  )}
                  <button onClick={() => handleUpdateStatus(selectedTicket, 'closed')} style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.4rem 0.85rem', borderRadius:'0.5rem', border:'1px solid rgba(148,163,184,0.35)', background:'rgba(148,163,184,0.08)', color:'#94a3b8', fontSize:'0.75rem', fontWeight:700, cursor:'pointer', fontFamily:"'Cairo',sans-serif" }}>
                    {Icons.x} إغلاق التذكرة
                  </button>
                </>
              )}
            </div>

            {/* Customer Info */}
            {selectedTicket.customer_name && (
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.75rem', background:'rgba(10,186,181,0.06)', borderRadius:'0.625rem', border:'1px solid rgba(10,186,181,0.15)' }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(10,186,181,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="2" style={{width:18,height:18}}><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.875rem', color:'#1E3A3A' }}>{selectedTicket.customer_name}</div>
                  {selectedTicket.customer_phone && <div style={{ fontSize:'0.75rem', color:'#737373', direction:'ltr' }}>{selectedTicket.customer_phone}</div>}
                </div>
                <span style={{ marginRight:'auto', fontSize:'0.72rem', background:'rgba(10,186,181,0.12)', color:'#0ABAB5', padding:'0.2rem 0.6rem', borderRadius:'1rem', fontWeight:600 }}>عميل</span>
              </div>
            )}

            {selectedTicket.description && (
              <p className="watrly-tickets__detail-desc">
                {selectedTicket.category && (
                  <span style={{ display:'inline-block', marginLeft:'0.5rem', fontSize:'0.72rem', background:'rgba(10,186,181,0.1)', color:'#0ABAB5', border:'1px solid rgba(10,186,181,0.25)', borderRadius:999, padding:'0.1rem 0.5rem', fontWeight:700, verticalAlign:'middle' }}>
                    {getCategoryLabel(selectedTicket.category)}
                  </span>
                )}
                {selectedTicket.description}
              </p>
            )}
            
            <div className="watrly-tickets__messages" ref={msgsContainerRef}>
              <h4>المحادثة</h4>
              {selectedTicket.messages?.length > 0 ? (
                selectedTicket.messages.map((msg, idx) => {
                  const atts = msg.attachments || [];
                  return (
                    <div key={idx} className={`watrly-tickets__message watrly-tickets__message--${msg.sender_type}`}>
                      <span className="watrly-tickets__message-sender">{['admin','agent','system','bot'].includes(msg.sender_type) ? 'فريق الدعم' : msg.sender_type === 'customer' ? 'العميل' : msg.sender_type === 'driver' ? 'السائق' : 'فريق الدعم'}</span>
                      {msg.message && <p style={{ margin:0 }}>{renderText(msg.message)}</p>}
                      {atts.length > 0 && (
                        <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem', marginTop:'0.5rem' }}>
                          {atts.map((att, ai) => (
                            att.type?.startsWith('image/')
                              ? <div key={ai} style={{ position:'relative', display:'inline-block', cursor:'zoom-in' }} onClick={() => {
                                  const w = window.open(''); w.document.write(`<html><body style="margin:0;background:#000;display:flex;justify-content:center;align-items:center;min-height:100vh"><img src="${att.data_url}" style="max-width:100%;max-height:100vh;object-fit:contain"/></body></html>`);
                                }}>
                                  <img src={att.data_url} alt={att.name} style={{ maxWidth:'100%', maxHeight:200, borderRadius:'0.6rem', objectFit:'cover', display:'block', border:'1px solid rgba(0,0,0,0.1)' }}/>
                                  <div style={{ position:'absolute', bottom:4, right:4, background:'rgba(0,0,0,0.45)', borderRadius:4, padding:'1px 5px', fontSize:'0.6rem', color:'#fff' }}>🔍</div>
                                </div>
                              : att.type === 'application/pdf'
                                ? <div key={ai} style={{ borderRadius:'0.6rem', overflow:'hidden', border:'1px solid rgba(10,186,181,0.2)' }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.3rem 0.6rem', background:'rgba(10,186,181,0.06)', fontSize:'0.7rem', color:'#0ABAB5', fontWeight:700 }}>
                                      📄 {att.name}
                                      <a href={att.data_url} download={att.name} style={{ marginRight:'auto', color:'#0ABAB5', fontSize:'0.65rem' }}>تحميل</a>
                                    </div>
                                    <iframe src={att.data_url} title={att.name} style={{ width:'100%', height:180, border:'none', background:'#fff' }}/>
                                  </div>
                                : <a key={ai} href={att.data_url} download={att.name} style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontSize:'0.72rem', color:'#0ABAB5', background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.2)', borderRadius:'0.4rem', padding:'0.3rem 0.55rem', textDecoration:'none' }}>
                                    📎 {att.name}
                                  </a>
                          ))}
                        </div>
                      )}
                      <span className="watrly-tickets__message-time">{formatDate(msg.created_at)}</span>
                    </div>
                  );
                })
              ) : (
                <p className="watrly-tickets__no-messages">لا توجد رسائل</p>
              )}
              <div ref={msgBottomRef} />
            </div>

            {selectedTicket.status !== 'closed' && /* resolved allowed */ (
              <div className="watrly-tickets__reply" style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                {adminFiles.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'0.35rem', padding:'0.4rem 0' }}>
                    {adminFiles.map((f, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.3rem', background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.2)', borderRadius:'0.4rem', padding:'0.2rem 0.5rem', fontSize:'0.72rem', color:'#0ABAB5' }}>
                        {f.name}
                        <button onClick={() => setAdminFiles(p => p.filter((_,j)=>j!==i))} style={{ background:'none', border:'none', cursor:'pointer', color:'#999', fontSize:12 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:'0.5rem', alignItems:'flex-end' }}>
                  <label style={{ cursor:'pointer', padding:'0.5rem', border:'1px solid #e5e5e5', borderRadius:'0.5rem', display:'flex', alignItems:'center' }} title="إرفاق ملف">
                    📎
                    <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" onChange={async e => {
                      const selected = Array.from(e.target.files || []);
                      e.target.value = '';
                      const check = await validateFiles(selected);
                      if (!check.safe) { toast.error(check.reason); return; }
                      setAdminFiles(p => [...p, ...selected].slice(0, 5));
                    }} style={{ display:'none' }}/>
                  </label>
                  <textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="اكتب ردك هنا..." style={{ flex:1 }}/>
                  <Button variant="primary" onClick={handleAddMessage} loading={processing}>{Icons.send} إرسال</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="تذكرة جديدة" size="md"
        footer={<div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setShowCreateModal(false)}>إلغاء</Button>
          <Button variant="primary" onClick={handleCreateTicket} loading={processing}>إنشاء</Button>
        </div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="الموضوع" value={newTicket.subject} onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })} required />
          <div>
            <label style={{ fontSize: '0.875rem', color: '#737373', marginBottom: '0.25rem', display: 'block' }}>الوصف</label>
            <textarea value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} placeholder="وصف المشكلة..." style={{ width: '100%', minHeight: '100px', padding: '0.75rem', border: '1px solid #E5E5E5', borderRadius: '0.5rem' }} />
          </div>
          <Dropdown label="الفئة" options={categoryOptions.filter(o => o.value !== 'all')} value={newTicket.category} onChange={(val) => setNewTicket({ ...newTicket, category: val })} />
          <Dropdown label="الأولوية" options={priorityOptions.filter(o => o.value !== 'all')} value={newTicket.priority} onChange={(val) => setNewTicket({ ...newTicket, priority: val })} />
        </div>
      </Modal>
    </div>
  );
};

export default TicketsPage;
